import React, { useMemo, useRef, useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { Table } from '@/components/design-system/molecules/table';

import { ColocAPIConnectedTable } from '@/features/shared/feature-coloc-api-connected-table';
import { PayoutsSortType } from '@/lib/coloc-api/admin';
import { ColocAPIConnectedTableImperativeMethods } from '@/features/shared/feature-coloc-api-connected-table/types';

import { Payout } from '@/features/admin/payouts/types';
import { useGetOutgoingTransactionsListContext } from '../../hooks/use-get-outgoing-transactions';
import { getFullDate } from '@/utils/util_helpers/formatting/dates';
import { OutgoingTransactionStatusCellContent } from './components/outgoing-transaction-status-cell-content';
import { OutgoingTransactionsRow } from './components/outgoing-transactions-row';
import { OutgoingTransactionAmountCellContent } from './components/outgoing-transaction-amount-cell-content';
import { PayoutRentDetailModal } from '@/features/shared/payout-rent-detail-modal';
import { PopUp } from '@/components/design-system/molecules/pop-up';
import { Modal } from '@/components/design-system/atoms/modal';
import { BulkPaymentConfirmationModal } from '@/features/admin/transactions/components/bulk-payment-confirmation-modal';
import { useGetOutgoingTransactionPaymentContext } from '../../hooks/use-get-outgoing-transaction-payment';
import { DiscussionThreadTableCellContent } from '@/features/shared/discussion-threads/components/discussion-thread-table-cell-content';
import { useDiscussionThreadProviderContext } from '@/features/shared/discussion-threads/hooks/use-discussion-thread';
import { ColocApiConnectedTableBulkActionDeclaration } from '@/features/shared/feature-coloc-api-connected-table/types';
import { ColocationPayment } from '@/features/admin/transactions/types/colocation-payment';
import { useUpdatePayments } from '@/features/admin/transactions/hooks/use-update-payouts';

const OutgoingTransactionsListTable = () => {
  const columnHelper = createColumnHelper<Payout>();
  const { t } = useTranslation();
  const tableRef = useRef<ColocAPIConnectedTableImperativeMethods>(null);
  const { showTransactionDetailsModal, toggleShowTransactionDetailsModal } =
    useGetOutgoingTransactionsListContext();
  const { openDiscussionThread } = useDiscussionThreadProviderContext()!;
  const {
    showFetchedPaymentSuccessModal,
    toggleShowFetchedPaymentSuccessModal,
    showFetchedPaymentFailedModal,
    hideErrorModal,
    setPaymentId,
  } = useGetOutgoingTransactionPaymentContext()!;

  const { mutateAsync, fetchPreview } = useUpdatePayments();

  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [bulkPreviewGroups, setBulkPreviewGroups] = useState<any[]>([]);
  const [bulkSelectedIds, setBulkSelectedIds] = useState<number[]>([]);

  const getSelectedPaymentIds = () =>
    (tableRef.current?.getSelectedOriginals() ?? []).map((o: ColocationPayment) => o.id);

  const handleBulkPayment = async () => {
    const selectedPaymentIds = getSelectedPaymentIds();
    if (!selectedPaymentIds || selectedPaymentIds.length === 0) {
      return;
    }

    const groups = await fetchPreview(selectedPaymentIds as number[]);
    if (!groups || (Array.isArray(groups) && groups.length === 0)) {
      return;
    }
    setBulkPreviewGroups(groups as any[]);
    setBulkSelectedIds(selectedPaymentIds as number[]);
    setIsBulkModalOpen(true);
  };

  const actions: Array<ColocApiConnectedTableBulkActionDeclaration> = useMemo(() => [
    {
      text: t('js.features.admin.transactions.outgoing_transactions_list.actions.bulk_payment'),
      iconName: 'money-send' as const,
      onClick: handleBulkPayment
    }
  ], [t, handleBulkPayment]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('created_at', {
        id: 'created_at',
        cell: (info) => (
          <Table.TableCellDefaultContent className="w-[85px]">
            {getFullDate(new Date(info.getValue()))}
          </Table.TableCellDefaultContent>
        ),
        header: () =>
          t('js.features.admin.transactions.outgoing_transactions_list.table_columns.date'),
        footer: (info) => info.column.id,
      }),
      columnHelper.accessor('name', {
        id: 'house_name',
        cell: (info) => {
          const value = info.getValue() as string | null;
          return (
            <Table.TableCellDefaultContent className="min-w-[220px] whitespace-nowrap">
              {info.row.original.room_number
                ? `${value ?? ''} - ${info.row.original.room_number}`
                : value ?? ''}
            </Table.TableCellDefaultContent>
          );
        },
        header: () =>
          t('js.features.admin.transactions.outgoing_transactions_list.table_columns.house'),
        footer: (info) => info.column.id,
        meta: {
          className: 'min-w-[220px]',
          headerClassName: 'min-w-[220px]',
        },
      }),
      columnHelper.accessor('discussion_thread', {
        id: 'discussion_thread',
        cell: (info) => (
          <DiscussionThreadTableCellContent
            messages_count={info.getValue()?.messages_count || 0}
            onClick={() => {
              openDiscussionThread({
                type: 'ColocationPayment',
                ...(info.getValue()?.id && info.getValue()?.id !== 'null'
                  ? { discussionThreadId: Number(info.getValue()?.id) }
                  : { discussableId: Number(info.row.original.id) }),
                onMessageSent: () => {
                  // Refresh the payout data after a message is sent
                  tableRef.current?.refetch();
                },
              });
            }}
          />
        ),
        header: () =>
          t('js.features.admin.contracts.room_assignment.table_columns.discussion_thread'),
        footer: (info) => info.column.id,
        enableSorting: false,
        enableColumnFilter: false,
        meta: {
          className: 'w-[80px]',
          headerClassName: 'w-[80px]',
        },
      }),
      columnHelper.accessor('recipient.name', {
        id: 'recipient',
        cell: (info) => (
          <Table.TableCellDefaultContent className="min-w-[240px] whitespace-nowrap">
            {info.getValue()}
          </Table.TableCellDefaultContent>
        ),
        header: () =>
          t('js.features.admin.transactions.outgoing_transactions_list.table_columns.recipient'),
        footer: (info) => info.column.id,
        meta: {
          className: 'min-w-[240px]',
          headerClassName: 'min-w-[240px]',
        },
      }),
      columnHelper.accessor('category', {
        id: 'category',
        cell: (info) => (
          <Table.TableCellDefaultContent className="w-[110px]">
            {t(
              `js.features.admin.transactions.outgoing_transactions_list.table_cells.categories.${info.getValue()}`
            )}
          </Table.TableCellDefaultContent>
        ),
        header: () =>
          t('js.features.admin.transactions.outgoing_transactions_list.table_columns.category'),
        footer: (info) => info.column.id,
        meta: {
          className: 'w-[110px]',
          headerClassName: 'w-[110px]',
        },
      }),
      columnHelper.accessor('amount', {
        id: 'amount',
        cell: (info) => (
          <OutgoingTransactionAmountCellContent
            amount={info.getValue()}
            // openDetailsModal={() =>
            //   toggleShowTransactionDetailsModal({
            //     show: true,
            //     payout: info.row.original,
            //   })
            // }
          />
        ),
        header: () =>
          t('js.features.admin.transactions.outgoing_transactions_list.table_columns.amount'),
        footer: (info) => info.column.id,
      }),
      columnHelper.accessor('status', {
        id: 'status',
        cell: (info) => <OutgoingTransactionStatusCellContent status={info.getValue()} />,
        header: () =>
          t('js.features.admin.transactions.outgoing_transactions_list.table_columns.status'),
        footer: (info) => info.column.id,
        meta: {
          className: 'w-[110px]',
          headerClassName: 'w-[110px]',
        },
      }),
      // columnHelper.accessor('actor', {
      //   id: 'actor',
      //   cell: (info) => (
      //     <Table.TableCellDefaultContent>
      //       {info.getValue().type} #{info.getValue().id}
      //     </Table.TableCellDefaultContent>
      //   ),
      //   header: () =>
      //     t('js.features.admin.transactions.outgoing_transactions_list.table_columns.actor'),
      //   footer: (info) => info.column.id,
      // }),
      columnHelper.display({
        id: 'accordion',
        cell: ({ row }) => (
          <Table.TableCellAccordionContent
            onClick={() => row.toggleExpanded()}
            open={row.getIsExpanded()}
          />
        ),
        footer: (info) => info.column.id,
      }),
    ],
    [columnHelper, t, toggleShowTransactionDetailsModal, openDiscussionThread]
  );

  return (
    <>
      <ColocAPIConnectedTable<Payout, PayoutsSortType>
        ref={tableRef}
        className="mb-5"
        columns={columns}
        dataSource={useGetOutgoingTransactionsListContext()!}
        actions={actions}
        enableMultiRowSelection
        enableRowSelection={(row) => row.original.status === 'unknown'}
        renderSubComponent={(row) => (
          <OutgoingTransactionsRow outgoingTransaction={row.row.original} index={row.row.index} />
        )}
      />

      <BulkPaymentConfirmationModal
        isOpen={isBulkModalOpen}
        groups={bulkPreviewGroups}
        onClose={() => setIsBulkModalOpen(false)}
        onConfirm={async () => {
          if (!bulkSelectedIds || bulkSelectedIds.length === 0) return;
          await mutateAsync(bulkSelectedIds as number[]);
          tableRef.current?.refetch();
        }}
      />

      {showTransactionDetailsModal.show && showTransactionDetailsModal.payout && (
        <PayoutRentDetailModal
          payout={showTransactionDetailsModal.payout}
          open={showTransactionDetailsModal.show}
          onClose={() =>
            toggleShowTransactionDetailsModal({
              show: false,
              payout: undefined,
            })
          }
        />
      )}

      {showFetchedPaymentSuccessModal && (
        <Modal
          open={showFetchedPaymentSuccessModal.show}
          onClose={() =>
            toggleShowFetchedPaymentSuccessModal({
              show: false,
              payment: undefined,
            })
          }
        >
          <PopUp
            type="feedback"
            title={
              t(
                'js.features.admin.transactions.outgoing_transactions_list.payout_confirmation.success_title'
              ) ?? ''
            }
            description={
              t(
                'js.features.admin.transactions.outgoing_transactions_list.payout_confirmation.success_description'
              ) ?? ''
            }
            primaryButtonProps={{
              onClick: () =>
                toggleShowFetchedPaymentSuccessModal({
                  show: false,
                  payment: undefined,
                }),
              title:
                t(
                  'js.features.admin.transactions.outgoing_transactions_list.payout_confirmation.success_confirmation_button'
                ) ?? '',
            }}
          />
        </Modal>
      )}

      {showFetchedPaymentFailedModal && showFetchedPaymentFailedModal.show && (
        <Modal open={showFetchedPaymentFailedModal.show} onClose={() => hideErrorModal()}>
          <PopUp
            type="error"
            title={showFetchedPaymentFailedModal.title ?? ''}
            description={showFetchedPaymentFailedModal.text ?? ''}
            primaryButtonProps={{
              onClick: () => {
                showFetchedPaymentFailedModal.retryFetching &&
                  setPaymentId(showFetchedPaymentFailedModal.payment_id);
                hideErrorModal();
              },
              title: showFetchedPaymentFailedModal.retryFetching
                ? t(
                    'js.features.admin.transactions.outgoing_transactions_list.payout_confirmation.generic_retry_button'
                  ) ?? ''
                : t(
                    'js.features.admin.transactions.outgoing_transactions_list.payout_confirmation.generic_ok_button'
                  ) ?? '',
            }}
            {...(showFetchedPaymentFailedModal.retryFetching && {
              secondaryButtonProps: {
                onClick: () => hideErrorModal(),
                title:
                  t(
                    'js.features.admin.transactions.outgoing_transactions_list.payout_confirmation.generic_cancel_button'
                  ) ?? '',
              },
            })}
          />
        </Modal>
      )}
    </>
  );
};

export { OutgoingTransactionsListTable };
